/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React from 'react';
import { Issue, IssueStatus } from '@shared/utils/issues';
import { Trash2, Calendar, Package, Hammer, Loader, FileText } from 'lucide-react';
import { ReleaseDetails } from './ReleaseDetails';
import { AcceptanceReportSection } from './AcceptanceReportSection';
import type { AcceptanceReportSummary } from '@shared/types/acceptance-reports';

interface ReleaseCardProps {
  release: Issue;
  isExpanded: boolean;
  isEditing: boolean;
  isUpdating: boolean;
  editedFields: Partial<Issue>;
  statusOptions: IssueStatus[];
  onToggleExpansion: () => void;
  onDelete: (releaseId: string) => void;
  onBuild: (release: Issue) => void;
  isBuilding: boolean;
  getStatusColor: (status: string) => string;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onSaveEdits: () => void;
  onFieldChange: (field: keyof Issue, value: any) => void;
  onStatusUpdate: (newStatus: IssueStatus) => void;
  onAddLink: () => void;
  onDeleteLink: (linkIndex: number) => void;
  acceptanceReport?: AcceptanceReportSummary | null;
  isGeneratingReport?: boolean;
  onGenerateReport?: (release: Issue) => void;
  onStoryClick?: (storyId: string, epicId: string) => void;
}

export function ReleaseCard({ 
  release, 
  isExpanded,
  isEditing,
  isUpdating,
  editedFields,
  statusOptions,
  onToggleExpansion,
  onDelete, 
  onBuild, 
  isBuilding, 
  getStatusColor,
  onStartEditing,
  onCancelEditing,
  onSaveEdits,
  onFieldChange,
  onStatusUpdate,
  onAddLink,
  onDeleteLink,
  acceptanceReport,
  isGeneratingReport = false,
  onGenerateReport,
  onStoryClick,
}: ReleaseCardProps) {
  const releaseDate = release.createdAt ? new Date(release.createdAt) : null;
  
  // Extract the release ID for use as DOM id (e.g., "REL-001" from "REL-001: Title")
  const releaseId = release.id.split(':')[0].trim();
  
  return (
    <div style={{
      position: 'relative',
      paddingLeft: '40px',
      marginBottom: '32px'
    }}>
      {/* Timeline dot */}
      <div style={{
        position: 'absolute',
        left: '0',
        top: '8px',
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        background: getStatusColor(release.status),
        border: '3px solid white',
        boxShadow: '0 0 0 2px ' + getStatusColor(release.status),
        zIndex: 2
      }} />
      
      {/* Timeline line */}
      <div style={{
        position: 'absolute',
        left: '7px',
        top: '24px',
        bottom: '-32px',
        width: '2px',
        background: '#e0e0e0',
        zIndex: 1
      }} />
      
      {/* Release card */}
      <div 
        id={releaseId}
        style={{
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '20px',
          background: 'white',
          cursor: 'pointer',
          transition: 'box-shadow 0.2s, background-color 0.5s',
        }}
        onClick={(e) => {
          // Don't expand if clicking on buttons
          if ((e.target as HTMLElement).closest('button')) {
            return;
          }
          onToggleExpansion();
        }}
        onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)'}
        onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Package size={18} style={{ color: '#0066cc' }} />
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                <span style={{ color: '#0066cc', fontFamily: 'monospace' }}>{release.id.split(':')[0]}</span>
                {': '}
                {release.title}
              </h3>
            </div>
            
            {release.version && (
              <div style={{ 
                display: 'inline-block',
                background: '#f0f0f0',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '13px',
                fontWeight: '600',
                color: '#666',
                marginBottom: '8px'
              }}>
                v{release.version}
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '12px', fontSize: '13px', color: '#666', marginTop: '8px' }}>
              {releaseDate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Calendar size={14} />
                  {releaseDate.toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </div>
              )}
              <div style={{
                padding: '2px 8px',
                borderRadius: '4px',
                background: getStatusColor(release.status) + '20',
                color: getStatusColor(release.status),
                fontWeight: '500'
              }}>
                {release.status}
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => onBuild(release)}
              disabled={isBuilding}
              className="btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: '#7c3aed',
                borderColor: '#7c3aed',
                color: 'white',
                minWidth: '80px',
                justifyContent: 'center',
                border: 'none',
                borderRadius: '4px',
                cursor: isBuilding ? 'not-allowed' : 'pointer',
                opacity: isBuilding ? 0.7 : 1
              }}
              title="Build this release using AI coding agent"
            >
              {isBuilding ? (
                <>
                  <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
                  Building
                </>
              ) : (
                <>
                  <Hammer size={12} />
                  Build
                </>
              )}
            </button>
            {onGenerateReport && (
              <button
                onClick={() => onGenerateReport(release)}
                disabled={isGeneratingReport}
                className="btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  backgroundColor: '#0891b2',
                  borderColor: '#0891b2',
                  color: 'white',
                  minWidth: '80px',
                  justifyContent: 'center',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isGeneratingReport ? 'not-allowed' : 'pointer',
                  opacity: isGeneratingReport ? 0.7 : 1
                }}
                title="Generate acceptance criteria report for this release"
              >
                {isGeneratingReport ? (
                  <>
                    <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
                    Report
                  </>
                ) : (
                  <>
                    <FileText size={12} />
                    Report
                  </>
                )}
              </button>
            )}
            <button
              onClick={() => onDelete(release.id)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: '#dc2626',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Delete release"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        {release.description && (
          <p style={{ fontSize: '14px', color: '#444', marginBottom: '12px', lineHeight: '1.5' }}>
            {release.description}
          </p>
        )}

        {release.labels && release.labels.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '12px' }}>
            {release.labels.map((label, index) => (
              <span
                key={index}
                style={{
                  background: '#e5e7eb',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#374151'
                }}
              >
                {label}
              </span>
            ))}
          </div>
        )}

        {release.assignedTo && !isExpanded && (
          <div style={{ 
            fontSize: '12px', 
            color: '#666', 
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: '1px solid #eee'
          }}>
            <strong>Assigned to:</strong> {release.assignedTo}
          </div>
        )}

        {/* Expanded view */}
        {isExpanded && (
          <>
            <ReleaseDetails
              release={release}
              isEditing={isEditing}
              isUpdating={isUpdating}
              editedFields={editedFields}
              statusOptions={statusOptions}
              getStatusColor={getStatusColor}
              onStartEditing={onStartEditing}
              onCancelEditing={onCancelEditing}
              onSaveEdits={onSaveEdits}
              onFieldChange={onFieldChange}
              onStatusUpdate={onStatusUpdate}
              onAddLink={onAddLink}
              onDeleteLink={onDeleteLink}
            />
            {(acceptanceReport || isGeneratingReport) && onGenerateReport && (
              <AcceptanceReportSection
                report={acceptanceReport ?? null}
                isGenerating={isGeneratingReport}
                onRefresh={() => onGenerateReport(release)}
                onStoryClick={onStoryClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

